import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import { UserContext } from '../../providers'
import {
  Box,
  Button,
  Heading,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  IconButton,
  Icon,
} from '@chakra-ui/react'
import { RiDeleteBin2Line } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import { useTable, Column } from 'react-table'
import { MainContentHeadingBox } from '../Base/MainContent'
import PermissionGuard from '../../components/PermissionGuard'
import { PermissionsEnum } from '../../../utils'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import { checkPermission } from '../../../utils/permissions'
import moment from 'moment'
import UrlEndpointsTextField from '../../components/UrlEndpoints/TextInputField'
import CodeEditor from '../../components/PathPolicy/CodeEditor'

interface DraftFunction {
  title: string
  description: string
  lastModified: number
  associatedUrlEndpoints: Array<{ patternId: string; urlPrefix: string }>
}

const formatDate = (timestamp?: number) =>
  timestamp ? moment(timestamp).format('MMM D, YYYY [at] hh:mm a') : '—'

const PLACEHOLDER_HANDLER = `function handler(url, urlPrefix, context) {
  return { url };
}
  
module.exports.handler = handler;
`

const CreateDraftSchema = Yup.object({
  title: Yup.string()
    .trim()
    .required('Title is required')
    .max(100, 'Title must be less than 100 characters')
    .matches(
      /^[a-zA-Z0-9-]+$/,
      'Title can only contain alphanumeric characters and hyphens',
    ),
  description: Yup.string()
    .trim()
    .required('Description is required')
    .max(1000, 'Description must be less than 1000 characters'),
  code: Yup.string().trim().required('Code is required'),
})

const UrlEndpointFunctionLayout: React.FC = () => {
  const toast = useToast()
  const { user } = useContext(UserContext)
  const isSuperAdmin =
    checkPermission({
      user,
      permission: PermissionsEnum.CLIENT_MANAGE,
    }) || window.user?.isSimulation

  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure()

  const {
    isOpen: isCreateModalOpen,
    onOpen: openCreateModal,
    onClose: closeCreateModal,
  } = useDisclosure()

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [drafts, setDrafts] = useState<DraftFunction[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleteLoading, setIsDeleteLoading] = useState(false)
  const [titleToDelete, setTitleToDelete] = useState<string | null>(null)

  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const { t } = useTranslation()
  const history = useHistory()

  const fetchDrafts = useCallback(async () => {
    if (!user?.clientNumber) return
    setIsLoading(true)
    try {
      const data = await request.get(
        `/api/clients/${user?.clientNumber}/url-endpoint-functions`,
      )
      if (request.error) {
        throw data
      }
      setDrafts(
        data.map(
          ({ title, description, lastModified, associatedUrlEndpoints }) => ({
            title,
            description,
            lastModified,
            associatedUrlEndpoints,
          }),
        ),
      )
    } catch (err: any) {
      toast({
        description: err?.message || 'Something went wrong.',
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } finally {
      setIsLoading(false)
    }
  }, [user?.clientNumber])

  useEffect(() => {
    fetchDrafts()
  }, [fetchDrafts])

  const handleDelete = useCallback(
    async (title: string | null) => {
      if (!user?.clientNumber || !title) return
      try {
        setIsDeleteLoading(true)
        const res = await request.delete(
          `/api/clients/${
            user?.clientNumber
          }/url-endpoint-functions/${encodeURIComponent(title)}`,
        )
        if (request.error) {
          throw res
        }
        toast({
          description: `Deleted "${title}" function successfully.`,
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        fetchDrafts()
      } catch (err: any) {
        toast({
          description:
            err?.message || err?.error || 'Unable to delete function.',
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } finally {
        setIsDeleteLoading(false)
        onDeleteAlertClose()
      }
    },
    [user?.clientNumber, fetchDrafts],
  )

  const columns = useMemo<Column<DraftFunction>[]>(
    () => [
      {
        Header: 'Title',
        accessor: 'title',
        Cell: ({ value }: { value: string }) => (
          <Button
            as={RouterLink}
            variant="link"
            maxWidth="250px"
            minWidth="100px"
            whiteSpace="normal"
            wordBreak="break-word"
            cursor="pointer"
            color="appClearBlue"
            textAlign="left"
            justifyContent="flex-start"
            to={`/settings/url-endpoint-functions/${encodeURIComponent(value)}`}
          >
            {value}
          </Button>
        ),
      },
      {
        Header: 'Description',
        accessor: 'description',
        Cell: ({ value }: { value: string }) => (
          <Text
            minWidth="150px"
            maxWidth="400px"
            whiteSpace="normal"
            wordBreak="break-word"
            lineHeight="1.5"
          >
            {value || '—'}
          </Text>
        ),
      },
      {
        Header: 'Last modified',
        accessor: 'lastModified',
        Cell: ({ value }: { value: number }) => (
          <Text>{formatDate(value)}</Text>
        ),
      },
      {
        Header: 'Associated URL-endpoints',
        accessor: 'associatedUrlEndpoints',
        Cell: ({
          value,
        }: {
          value?: Array<{ patternId: string; urlPrefix?: string }>
        }) => {
          const endpoints = value || []
          if (!endpoints.length) return <Text>—</Text>
          return (
            <VStack align="flex-start" spacing={1}>
              {endpoints.map(({ urlPrefix, patternId }, idx) => {
                if (urlPrefix === '' && typeof patternId === 'string') {
                  urlPrefix = 'default'
                }
                return (
                  <Button
                    key={`${patternId}-${idx}`}
                    as={RouterLink}
                    variant="link"
                    cursor="pointer"
                    color="appClearBlue"
                    to={`/url-endpoints/${patternId}`}
                  >
                    {urlPrefix}
                  </Button>
                )
              })}
            </VStack>
          )
        },
      },
      {
        Header: 'Delete',
        id: 'delete',
        Cell: ({ row }: { row: { original: DraftFunction } }) => {
          const { title, associatedUrlEndpoints = [] } = row.original
          const disabled = !isSuperAdmin || associatedUrlEndpoints.length > 0
          const tooltip = !isSuperAdmin
            ? 'You do not have permission to delete this function.'
            : associatedUrlEndpoints.length
            ? 'This function is associated with one or more URL endpoints.'
            : ''
          return (
            <Tooltip label={tooltip} disabled={!disabled} shouldWrapChildren>
              <IconButton
                padding="0px"
                margin="0px"
                minWidth="0px"
                aria-label="Delete URL Endpoint Function"
                isDisabled={disabled}
                icon={<Icon as={RiDeleteBin2Line} boxSize={5} />}
                variant="link"
                colorScheme={colors.appBrick}
                onClick={() => {
                  if (!disabled) {
                    setTitleToDelete(title)
                    onDeleteAlertOpen()
                  }
                }}
              />
            </Tooltip>
          )
        },
      },
    ],
    [handleDelete, isSuperAdmin],
  )

  const tableInstance = useTable({ columns, data: drafts })

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance

  return (
    <>
      <MainContentHeadingBox>
        <Heading
          as="h2"
          size="md"
          fontWeight="600"
          color={colors.appDarkIndigo}
        >
          {t('url_endpoint_functions', 'URL Endpoint Functions')}
        </Heading>

        {isSuperAdmin ? (
          <Button onClick={openCreateModal}>{t('add_new', 'Add New')}</Button>
        ) : (
          <></>
        )}
      </MainContentHeadingBox>

      <Box overflowX="auto" padding="16px">
        <Table {...getTableProps()} size="md">
          <Thead bgColor="appPaleGreyTwo">
            {headerGroups.map(headerGroup => {
              const { key: headerGroupKey, ...restHeaderGroupProps } =
                headerGroup.getHeaderGroupProps()
              return (
                <Tr
                  key={headerGroupKey ?? headerGroup.id}
                  {...restHeaderGroupProps}
                >
                  {headerGroup.headers.map(column => {
                    const { key: headerKey, ...restHeaderProps } =
                      column.getHeaderProps()
                    return (
                      <Th key={headerKey ?? column.id} {...restHeaderProps}>
                        {column.render('Header')}
                      </Th>
                    )
                  })}
                </Tr>
              )
            })}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {!isLoading ? (
              rows.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length} textAlign="center" py={6}>
                    No functions found.
                  </Td>
                </Tr>
              ) : (
                rows.map(row => {
                  prepareRow(row)
                  const { key: rowKey, ...restRowProps } = row.getRowProps()
                  return (
                    <Tr key={rowKey} {...restRowProps}>
                      {row.cells.map(cell => {
                        const { key: cellKey, ...restCellProps } =
                          cell.getCellProps()
                        return (
                          <Td key={cellKey} {...restCellProps}>
                            {cell.render('Cell')}
                          </Td>
                        )
                      })}
                    </Tr>
                  )
                })
              )
            ) : (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="appPaleGrey"
                    color="appBlue"
                    size="xl"
                  />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              <Flex alignItems="center">
                {t(
                  'delete_url_endpoint_function',
                  'Delete URL Endpoint Function',
                )}
              </Flex>
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text color="appBattleshipGrey">
                {t(
                  'remove_url_endpoint_function',
                  'Are you sure you want to delete the current URL endpoint function? This action cannot be undone.',
                )}
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter
              style={{ gap: '1em', borderTop: '1px solid #e7eaf0' }}
            >
              <Button
                ref={cancelRef}
                onClick={onDeleteAlertClose}
                disabled={isDeleteLoading}
                variant="outline"
              >
                {t('cancel', 'Cancel')}
              </Button>
              <Button
                colorScheme={colors.appRed}
                onClick={async () => await handleDelete(titleToDelete)}
                isLoading={isDeleteLoading}
                loadingText={t('deleting', 'Deleting...')}
              >
                {t('delete', 'Delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {isSuperAdmin && (
        <Formik
          initialValues={{
            title: '',
            description: '',
            code: PLACEHOLDER_HANDLER,
          }}
          validationSchema={CreateDraftSchema}
          validateOnMount
          onSubmit={async (values, actions) => {
            try {
              const res = await request.post(
                `/api/clients/${user?.clientNumber}/url-endpoint-functions`,
                {
                  title: values.title.trim(),
                  description: values.description.trim(),
                  code: values.code,
                },
              )
              if (request.error) throw res
              toast({
                description: t(
                  'draft_created',
                  'Draft version created successfully.',
                ),
                status: 'success',
                isClosable: true,
                duration: 4000,
                position: 'top-right',
                variant: 'outline',
              })
              actions.resetForm()
              closeCreateModal()
              history.push(
                `/settings/url-endpoint-functions/${encodeURIComponent(
                  values.title,
                )}`,
              )
            } catch (error: any) {
              toast({
                description:
                  error?.message ||
                  t('something_went_wrong', 'Something went wrong!'),
                status: 'error',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
            } finally {
              actions.setSubmitting(false)
            }
          }}
        >
          {({
            values,
            handleChange,
            setFieldValue,
            setFieldTouched,
            errors,
            touched,
            isSubmitting,
            resetForm,
            isValid,
          }) => {
            const handleModalClose = () => {
              resetForm()
              closeCreateModal()
            }
            return (
              <Modal
                size="4xl"
                isOpen={isCreateModalOpen}
                onClose={handleModalClose}
              >
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>
                    {t('add_new_function', 'Add new function')}
                  </ModalHeader>
                  <ModalCloseButton />
                  <Form>
                    <ModalBody>
                      <VStack align="stretch" spacing={6}>
                        <UrlEndpointsTextField
                          name="title"
                          label={t('title_2', 'Title')}
                          placeholder={t('enter_title', 'Enter title')}
                          description=""
                          value={values.title}
                          handleChange={handleChange}
                          required
                          isDisabled={isSubmitting}
                        />
                        <UrlEndpointsTextField
                          name="description"
                          label={t('description', 'Description')}
                          placeholder={t(
                            'enter_description',
                            'Enter description',
                          )}
                          description=""
                          value={values.description}
                          handleChange={handleChange}
                          required
                          isDisabled={isSubmitting}
                        />
                        <Box>
                          <Text mb={2} fontWeight="semibold">
                            {t('code', 'Code')}
                          </Text>
                          <CodeEditor
                            value={values.code}
                            placeholder={PLACEHOLDER_HANDLER}
                            onChange={val => {
                              setFieldValue('code', val ?? '')
                              setFieldTouched('code', true, false)
                            }}
                            height="250px"
                          />
                          {touched.code && errors.code && (
                            <Text mt={2} color="red.500" fontSize="sm">
                              {errors.code}
                            </Text>
                          )}
                        </Box>
                      </VStack>
                    </ModalBody>
                    <ModalFooter gap={4}>
                      <Button onClick={handleModalClose} variant="outline">
                        {t('cancel', 'Cancel')}
                      </Button>
                      <Button
                        type="submit"
                        colorScheme="appBlue"
                        isLoading={isSubmitting}
                        disabled={!isValid || isSubmitting}
                      >
                        {t('save', 'Save')}
                      </Button>
                    </ModalFooter>
                  </Form>
                </ModalContent>
              </Modal>
            )
          }}
        </Formik>
      )}
    </>
  )
}

export default UrlEndpointFunctionLayout
